import { astar } from '@0xtorch/core'
import { createClient } from '../../client/createClient'
import { createSubscan } from '../../explorer'
import type { Chain } from '../types'

type CreateAstarChainParameters = {
  explorerApiKey?: string
  explorerProxyUrl?: string
  rpcEndpoints?: string[]
}

export const createAstarChain = ({
  explorerApiKey,
  explorerProxyUrl,
  rpcEndpoints = astarWebsocketRpcUrls,
}: CreateAstarChainParameters): Chain => {
  return {
    /** {@link https://polkadot.subscan.io/parachain_list?status=parachain} */
    id: 2006,
    /** {@link https://github.com/paritytech/ss58-registry/blob/main/ss58-registry.json} */
    ss58Prefix: 5,
    network: 'astar-native',
    name: 'Astar (Native)',
    blockTimeSeconds: 8,
    nativeToken: {
      name: 'Astar',
      symbol: 'ASTR',
      decimals: 18,
      currency: astar,
    },
    client: createClient(rpcEndpoints),
    explorer: createSubscan({
      name: 'Subscan',
      baseUrl: 'https://astar.subscan.io',
      networkName: 'astar',
      apiKey: explorerApiKey,
      proxyUrl: explorerProxyUrl,
    }),
    rpcEndpoints,
    assetType: 'assets',
    /** {@link https://astar.subscan.io/asset_token?page_size=25&page=1&order=desc&order_field=holders} */
    assetMap: new Map([
      [
        18446744073709551633n,
        {
          asset: {
            symbol: 'PINK',
            type: 'CryptoCurrency',
            id: 'dot-finance',
            name: 'Dot Finance',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        340282366920938463463374607431768211455n,
        {
          asset: {
            symbol: 'DOT',
            type: 'CryptoCurrency',
            id: 'polkadot',
            name: 'Polkadot',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        4294969280n,
        {
          asset: {
            symbol: 'USDT',
            type: 'CryptoCurrency',
            id: 'tether',
            name: 'Tether',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        18446744073709551632n,
        {
          asset: {
            symbol: 'vASTR',
            type: 'CryptoCurrency',
            id: 'bifrost-voucher-astr',
            name: 'Bifrost Voucher ASTR',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        18446744073709551616n,
        {
          asset: {
            symbol: 'ACA',
            type: 'CryptoCurrency',
            id: 'acala',
            name: 'Acala',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551619n,
        {
          asset: {
            symbol: 'GLMR',
            type: 'CryptoCurrency',
            id: 'moonbeam',
            name: 'Moonbeam',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        18446744073709551623n,
        {
          asset: {
            symbol: 'BNC',
            type: 'CryptoCurrency',
            id: 'bifrost-native-coin',
            name: 'Bifrost Native Coin',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551617n,
        {
          asset: {
            symbol: 'aSEED',
            type: 'CryptoCurrency',
            id: 'ausd-seed-acala',
            name: 'aUSD SEED (Acala)',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551624n,
        {
          asset: {
            symbol: 'vDOT',
            type: 'CryptoCurrency',
            id: 'voucher-dot',
            name: 'Voucher DOT',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        18446744073709551621n,
        {
          asset: {
            symbol: 'INTR',
            type: 'CryptoCurrency',
            id: 'interlay',
            name: 'Interlay',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        18446744073709551622n,
        {
          asset: {
            symbol: 'PHA',
            type: 'CryptoCurrency',
            id: 'pha',
            name: 'PHALA',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551631n,
        {
          asset: {
            symbol: 'UNQ',
            type: 'CryptoCurrency',
            id: 'unique-network',
            name: 'Unique Network',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        18446744073709551618n,
        {
          asset: {
            symbol: 'LDOT',
            type: 'CryptoCurrency',
            id: 'liquid-staking-dot',
            name: 'Liquid Staking DOT',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        18446744073709551620n,
        {
          asset: {
            symbol: 'IBTC',
            type: 'CryptoCurrency',
            id: 'interbtc',
            name: 'interBTC',
            updatedAt: 0,
          },
          decimals: 8,
        },
      ],
      [
        100001n,
        {
          asset: {
            symbol: 'xcINJ',
            type: 'CryptoCurrency',
            id: 'injective-protocol',
            name: 'Injective',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        18446744073709551634n,
        {
          asset: {
            symbol: 'PEN',
            type: 'CryptoCurrency',
            id: 'pendulum-chain',
            name: 'Pendulum',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551630n,
        {
          asset: {
            symbol: 'HDX',
            type: 'CryptoCurrency',
            id: 'hydradx',
            name: 'Hydration',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551625n,
        {
          asset: {
            symbol: 'CLV',
            type: 'CryptoCurrency',
            id: 'clover-finance',
            name: 'Clover Finance',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        18446744073709551635n,
        {
          asset: {
            symbol: 'XLM.m',
            type: 'CryptoCurrency',
            id: 'stellar',
            name: 'Stellar',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        18446744073709551637n,
        {
          asset: {
            symbol: 'vGLMR',
            type: 'CryptoCurrency',
            id: 'voucher-glmr',
            name: 'Voucher GLMR',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        4294969281n,
        {
          asset: {
            symbol: 'USDC',
            type: 'CryptoCurrency',
            id: 'usd-coin',
            name: 'USDC',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
    ]),
  }
}

export const astarWebsocketRpcUrls = [
  'wss://rpc.astar.network',
  'wss://1rpc.io/astr',
  'wss://astar.public.blastapi.io',
  'wss://astar-rpc.dwellir.com',
  'wss://astar.api.onfinality.io/public-ws',
  'wss://astar.public.curie.radiumblock.co/ws',
]
